import { Comment } from '../../../entities/Comment/Comment'
import { DistrictDivision } from '../../../entities/DistrictDivision/DistrictDivision'
import { DistrictDivisionDistrict } from '../../../entities/DistrictDivisionDistrict/DistrictDivisionDistrict'
import { DistrictDivisionGroup } from '../../../entities/DistrictDivisionGroup/DistrictDivisionGroup'
import { PrintableBlockOffset } from '../contexts/PrintableBlockOffsetsContext'
import {
    DistrictDivisionGroupsPerColumn,
    DistrictDivisionGroupWithHeight,
    PrintableDistrictDivisionBlock,
    PrintableDistrictDivisionCommentBlock,
    PrintableDistrictDivisionDistrictBlock,
    PrintableDistrictDivisionPageColumn,
    PrintableDistrictDivisionPageInterface,
    PrintableDistrictDivisionTitleBlock,
} from '../models'

const initialDivisionGroupsPerColumn: DistrictDivisionGroupsPerColumn = [[], [], []]

export const transformDistrictDivisionDistrictToPrintableDistrictDivisionDistrictBlock = (
    district: DistrictDivisionDistrict,
    groupTitle?: string,
): PrintableDistrictDivisionDistrictBlock => ({
    type: 'printable-district-division-district-block',
    id: district.id,
    districtDivisionDistrict: district,
    groupTitle,
})

export const transformDistrictDivisionGroupToPrintableDistrictDivisionDistrictBlocks = (group: DistrictDivisionGroup): PrintableDistrictDivisionBlock[] => group.districtDivisionDistricts
    .reduce((total: PrintableDistrictDivisionBlock[], district, index) => [
        ...total,
        transformDistrictDivisionDistrictToPrintableDistrictDivisionDistrictBlock(district, index === 0 ? group.name : undefined),
    ], [])

export const getDistrictDivisionTitleBlock = (date: Date, isHidden: boolean, id: string): PrintableDistrictDivisionTitleBlock => ({
    type: 'printable-district-division-title-block',
    id,
    isHidden,
    date,
})

export const getDistrictDivisionCommentBlock = (comment: Comment): PrintableDistrictDivisionCommentBlock => ({
    type: 'printable-district-division-comment-block',
    id: comment.id,
    body: comment.body,
})

export const getPrintableDistrictDivisionDistrictBlocksPerColumn = (
    districtDivision: DistrictDivision,
    groups: DistrictDivisionGroup[],
    columnIndex: number,
    isLastColumn?: boolean,
): PrintableDistrictDivisionBlock[] => groups
    .reduce((total: PrintableDistrictDivisionBlock[], group, index) => {
        const hasTitleBlock = index === 0
        const comment = districtDivision.comments[0]?.body ? districtDivision.comments[0] : undefined
        const isLastGroup = index === groups.length - 1

        return [
            ...total,
            ...(hasTitleBlock ? [getDistrictDivisionTitleBlock(districtDivision.date, columnIndex !== 0, columnIndex.toString())] : []),
            ...transformDistrictDivisionGroupToPrintableDistrictDivisionDistrictBlocks(group),
            ...((isLastGroup && isLastColumn && comment) ? [getDistrictDivisionCommentBlock(comment)] : []),
        ]
    }, [])

const transformPrintableDistrictDivisionPageColumn = (id: string, blocks: PrintableDistrictDivisionBlock[]): PrintableDistrictDivisionPageColumn => ({
    id,
    blocks,
})

const transformPrintableDistrictDivisionPage = (id: string, columns: PrintableDistrictDivisionPageColumn[]): PrintableDistrictDivisionPageInterface => ({
    id,
    columns,
})


export const getTotalPrintableDistrictDivisionPages = (offsets: PrintableBlockOffset[], pageHeight: number) => {
    const lowestBlock = [...offsets]
        .sort((a, b) => (b.offsetTop + b.clientHeight) - (a.offsetTop + a.clientHeight))[0]
    const lowestBlockHeight = lowestBlock.clientHeight + lowestBlock.offsetTop

    return Math.ceil(lowestBlockHeight / pageHeight)
}

export const filterPrintableDistrictDivisionBlockByOffset = (
    block: PrintableDistrictDivisionBlock,
    offsets: PrintableBlockOffset[],
    pageStart: number,
    pageEnd: number,
): boolean => {
    const blockOffset = offsets.find(offset => offset.id === block.id)
    const offsetPlusHeight = (blockOffset?.offsetTop || 0) + (blockOffset?.clientHeight || 0)

    return offsetPlusHeight >= pageStart && offsetPlusHeight < pageEnd
}

/**
 * Only returns DistrictDivisionGroups, which have at least one DistrictDivisionDistrict with at least one DistrictDivisionDistrictPlanning.
 * All DistrictDivisionDistricts without at least one DistrictDivisionDistrictPlanning are removed as well.
 */
const filterOutEmptyDistrictDivisionGroups = (
    districtDivisionGroups: DistrictDivisionGroup[],
): DistrictDivisionGroup[] => districtDivisionGroups
    .map(group => ({
        ...group,
        districtDivisionDistricts: group.districtDivisionDistricts
            .filter(district => district.districtDivisionDistrictPlannings.length > 0),
    }))
    .filter(group => group.districtDivisionDistricts.length > 0)

/**
 * Creates a mapping of each DistrictDivisionGroup with it's rendered print height.
 */
const getDistrictLengthPerDistrict = (districtDivisionGroups: DistrictDivisionGroup[]): DistrictDivisionGroupWithHeight[] => districtDivisionGroups
    .map((group) => {
        // The group title has a height of 31.6 px.
        let height = 31.6

        group.districtDivisionDistricts
            .filter((district) => district.districtDivisionDistrictPlannings.length > 0)
            .forEach((district) => {
                // Each district has a title: 19.2 px, top padding: 8 px, list margin: 4 px, total 31.2 px.
                const districtTitleHeight = 31.2

                // Each employee planned per district has a height of 30 px.
                const heightPerPlannedEmployee = 30

                const employeeCount = district.districtDivisionDistrictPlannings.length

                height += districtTitleHeight + (employeeCount * heightPerPlannedEmployee)
            })

        return {
            id: group.id,
            height,
        }
    })

/**
 * Splits the DistrictDivisionGroups into 3 even columns, based on the heights of the DistrictDivisionGroups.
 */
function splitDistrictDivisionGroupsByHeight(
    districtDivisionGroups: DistrictDivisionGroup[],
    districtDivisionGroupsMappedByHeight: DistrictDivisionGroupWithHeight[],
): DistrictDivisionGroupsPerColumn {
    const districtDivisionGroupsById = Object.fromEntries(districtDivisionGroups.map(group => [group.id, group]))
    const mappedDistrictDivisionGroupsSortedByHeight = [...districtDivisionGroupsMappedByHeight].sort((a, b) => b.height - a.height)

    const result: DistrictDivisionGroup[][] = [[], [], []]
    const columnHeights = [0, 0, 0]

    mappedDistrictDivisionGroupsSortedByHeight.forEach(({ id, height }) => {
        const minIndex = columnHeights.indexOf(Math.min(...columnHeights))
        result[minIndex].push(districtDivisionGroupsById[id])
        columnHeights[minIndex] += height
    })

    return result as DistrictDivisionGroupsPerColumn
}

export const getPrintableDistrictDivisionPages = (districtDivision: DistrictDivision): PrintableDistrictDivisionPageInterface => {
    // First, divide all the districtDivisionGroups over 3 columns, filter out empty ones.
    const districtDivisionGroups = filterOutEmptyDistrictDivisionGroups(districtDivision.districtDivisionGroups)
    const districtLengthsPerDistrict = getDistrictLengthPerDistrict(districtDivisionGroups)
    const groupsPerColumn = splitDistrictDivisionGroupsByHeight(districtDivisionGroups, districtLengthsPerDistrict)
    const justifiedLength = groupsPerColumn.filter(groups => !! groups.length).length - 1

    // Then get all the PrintableDistrictDivisionBlocks for these 3 columns
    const columns = groupsPerColumn
        .map((groups, index) => {
            const blocks = getPrintableDistrictDivisionDistrictBlocksPerColumn(
                districtDivision,
                groups,
                index,
                index === justifiedLength,
            )

            return transformPrintableDistrictDivisionPageColumn(`column-${index}`, blocks)
        })

    const id = `${districtDivision.id}-${JSON.stringify(districtDivision).length}`

    return transformPrintableDistrictDivisionPage(`page-${id}`, columns)
}

export const transformPrintableDistrictDivisionColumnsToPrintableDistrictDivisionPages = (
    columns: PrintableDistrictDivisionPageColumn[],
    offsets: PrintableBlockOffset[],
): PrintableDistrictDivisionPageInterface[] => {
    if (offsets.length === 0) {
        return []
    }

    const pageHeight = 1270
    // Get the total amount of pages based on the offsets calculated from the sizer page.
    const totalPageAmount = getTotalPrintableDistrictDivisionPages(offsets, pageHeight)

    // Then fill these pages with 3 (empty) columns.
    const pages = new Array(totalPageAmount)
        .fill(null)
        .map((value, index) => {
            const pageColumns: PrintableDistrictDivisionPageColumn[] = initialDivisionGroupsPerColumn
                .map((columnValue, columnIndex) => transformPrintableDistrictDivisionPageColumn(`column-${columnIndex}`, []))

            return transformPrintableDistrictDivisionPage(`page-${index}`, pageColumns)
        })

    // Then fill every column of every page with all the blocks based on the offsets
    return pages.map((page, pageIndex) => {
        const newColumns = page.columns
            .map((column, columnIndex) => {
                const columnBlocks = columns[columnIndex].blocks
                    .filter((block) => filterPrintableDistrictDivisionBlockByOffset(
                        block,
                        offsets,
                        pageIndex * pageHeight,
                        (pageIndex + 1) * pageHeight,
                    ))

                return transformPrintableDistrictDivisionPageColumn(column.id, columnBlocks)
            })

        return transformPrintableDistrictDivisionPage(page.id, newColumns)
    })
}
